import React, { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Button,
  EmptyState,
  IconButton,
  IconMore,
  IconPlus,
  IconStore,
  Menu,
  MenuItem,
  ListPage,
  Pagination,
  StatusPill,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import type { TableSortState } from '../../utils/listOrdering';
import { TableSearch } from './TableSearch';
import {
  STORE_COLUMNS,
  type StoreListRow,
  type StoreSortKey,
  storeColumnSortKey,
  storeFlagLabel,
  storeFlagTone,
  storeTextValue,
} from './storesModel';

export interface StoresTabProps {
  stores: StoreListRow[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPage: (page: number) => void;
  onCreate: () => void;
  onEditStore: (storeId: number) => void;
  search?: string;
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sort?: TableSortState<StoreSortKey>;
  onSort?: (key: StoreSortKey) => void;
  filtering?: boolean;
}

const Placeholder: React.FC<{ value: React.ReactNode }> = ({ value }) =>
  value === '—' ? <span className="text-v2-fg-3">—</span> : <>{value}</>;

const StoreRowMenu: React.FC<{
  store: StoreListRow;
  onEditStore: (storeId: number) => void;
}> = ({ store, onEditStore }) => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);
  return (
    <span className="relative inline-block">
      <IconButton
        ref={anchor}
        size="sm"
        label={`Actions for ${store.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconMore />
      </IconButton>
      <Menu
        open={open}
        onClose={close}
        anchorRef={anchor}
        align="right"
        width={180}
      >
        <MenuItem
          onClick={() => {
            close();
            onEditStore(store.id);
          }}
        >
          Edit Store Info
        </MenuItem>
      </Menu>
    </span>
  );
};

const StoresTab: React.FC<StoresTabProps> = ({
  stores,
  loading,
  page,
  totalPages,
  totalCount,
  pageSize,
  onPage,
  onCreate,
  onEditStore,
  search = '',
  onSearchChange,
  sort = { key: null, direction: 'asc' },
  onSort,
  filtering = false,
}) => (
  <ListPage
    title="Stores"
    toolbar={
      <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
        <TableSearch
          label="Search stores"
          placeholder="Search name, city, phone, or knowledge base…"
          value={search}
          onChange={onSearchChange ?? (() => undefined)}
        />
      </div>
    }
    actions={
      <Button
        size="sm"
        variant="primary"
        leading={<IconPlus />}
        onClick={onCreate}
      >
        Create
      </Button>
    }
  >
    {!loading && stores.length === 0 ? (
      <EmptyState
        icon={<IconStore />}
        title={
          filtering
            ? 'No stores match that search'
            : "You don't have any stores at the moment"
        }
        description={
          filtering
            ? 'Try a store name, city, phone number, or knowledge base id.'
            : "When stores appear, they'll show up here."
        }
      />
    ) : (
      <>
        {loading && (
          <p role="status" className="sr-only">
            Loading...
          </p>
        )}
        <div className="v2-scroll overflow-x-auto">
          <Table className="min-w-[1200px]">
            <THead>
              <tr>
                {STORE_COLUMNS.map((column) => {
                  const sortKey = storeColumnSortKey(column);
                  return (
                    <TH
                      key={column.label}
                      align={column.kind === 'actions' ? 'right' : undefined}
                      sortable={Boolean(sortKey) && !loading}
                      active={Boolean(sortKey) && sort.key === sortKey}
                      dir={sort.direction}
                      onSort={() => sortKey && onSort?.(sortKey)}
                    >
                      {column.label}
                    </TH>
                  );
                })}
              </tr>
            </THead>
            <TBody>
              {loading ? (
                <TableSkeletonRows cols={STORE_COLUMNS.length} />
              ) : (
                stores.map((store) => (
                  <TR key={store.id}>
                    {STORE_COLUMNS.map((column) => {
                      switch (column.kind) {
                        case 'id':
                          return (
                            <TD key={column.label} num className="font-medium">
                              {store.id}
                            </TD>
                          );
                        case 'name':
                          return (
                            <TD key={column.label} className="font-medium">
                              {store.name}
                            </TD>
                          );
                        case 'text':
                          return (
                            <TD key={column.label}>
                              <Placeholder
                                value={storeTextValue(store, column.key)}
                              />
                            </TD>
                          );
                        case 'flag':
                          return (
                            <TD key={column.label}>
                              <StatusPill
                                tone={storeFlagTone(store[column.key])}
                              >
                                {storeFlagLabel(store[column.key])}
                              </StatusPill>
                            </TD>
                          );
                        case 'actions':
                          return (
                            <TD key={column.label} align="right">
                              <StoreRowMenu
                                store={store}
                                onEditStore={onEditStore}
                              />
                            </TD>
                          );
                        default:
                          return null;
                      }
                    })}
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </>
    )}

    {totalPages > 1 && (
      <Pagination
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPage={onPage}
        noun="stores"
      />
    )}
  </ListPage>
);

export default StoresTab;
